import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import CART_ITEM from '../../constants/cartItems';
import { getAlbumCustomPrintCartItemPrice } from '../../redux/helpers';
import TrashIcon from '../../../../components/icons/TrashIcon';
import { getAlbumCustomPrintName } from '../../../../util/custom-prints';
import { getIdentityName } from '../../../../util/identity';
import useIdentityJerseyColors from '../../../../apps/pro/views/HierarchyItem/Matches/hooks/useIdentityJerseyColors';
import { getCustomPrintPrintBackgrounds } from '../../../../util/custom-prints';
import IdentityAvatar from '../../../../containers/global/identity/IdentityAvatarContainer';
import CustomPrintIcon from '../../../../components/icons/CustomPrintIcon';
import {
  CUSTOM_PRINT_SUBTYPE,
  CUSTOM_PRINT_TYPE,
} from '../../../../constants/pro-custom-prints';
import { getAsset } from '../../../../util/assets';
import CollageIcon from '../../../../components/icons/CollageIcon';
import CustomPrintPlaqueConfiguratorModal from '../../../../components/global/CustomPrintConfigurator/CustomPrintPlaqueConfigurator/modal';
import Button from '../../../../components/global/Button';
import { validateCustomPrintPhotoSelections } from '../../../../components/global/CustomPrintConfigurator/CustomPrintPlaqueConfigurator/helpers';
import CheckoutCartItemPriceDisplay from './CheckoutCartItemPriceDisplay';
import { isAlbumCustomPrintConfigurableInPlace } from '../../../../apps/pro/views/HierarchyItem/Matches/components/PurchasingOptions/helpers';
import AccountCardCartItemPreview from './AccountCardCartItemPreview';
import AccountCardAlbumCustomPrintConfiguratorModal from '../../../../apps/pro/views/HierarchyItem/Matches/components/PurchasingOptions/components/AccountCardAlbumCustomPrintOption/AccountCardAlbumCustomPrintConfigurator/modal';
import CheckoutItemQuantityInput from './CheckoutItemQuantityInput';
import CartItemVendor from './CartItemVendor';
import { getThumbBySize } from '../../../../util/photo';
import { IMAGE_SIZE_CODE } from '../../../../constants/image-size-codes';
import CartItemPhotoPreview from './CartItemPhotoPreview';

const propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.oneOf([CART_ITEM.ALBUM_CUSTOM_PRINT]).isRequired,
    albumCustomPrint: PropTypes.shape({
      id: PropTypes.string.isRequired,
      custom_print: PropTypes.shape({
        name: PropTypes.string.isRequired,
      }).isRequired,
    }).isRequired,
    identity: PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  pricing: PropTypes.shape({
    price: PropTypes.object,
    discount_price: PropTypes.object,
  }),
  loading: PropTypes.bool,
  onRemove: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
};

const AlbumCustomPrintCartItem = ({
  item,
  pricing,
  loading,
  onRemove,
  onUpdate,
  watermarkSettings,
}) => {
  const [configurationModalOpen, setConfigurationModalOpen] = useState(false);
  const {
    albumCustomPrint,
    identity,
    quantity,
    isAllPhotos,
    configuration,
    forceEntitlement,
    prePaid: isPrepaid,
  } = item;
  const price = getAlbumCustomPrintCartItemPrice(item);

  // Colors only narrow an existing list, so the event having any backdrop at
  // all decides whether the identity lookup is worth a query.
  const hasEventBackdrops =
    getCustomPrintPrintBackgrounds(item.albumMembershipGroup).length > 0;
  const { jerseyColors } = useIdentityJerseyColors({
    identityId: identity?.id,
    albumFolderId:
      item.albumMembershipGroup?.album_folder?.id ??
      item.albumMembershipGroup?.album?.root_album_folder?.id,
    enabled: hasEventBackdrops,
  });
  const printBackgrounds = hasEventBackdrops
    ? getCustomPrintPrintBackgrounds(item.albumMembershipGroup, jerseyColors)
    : [];
  const photosNo = albumCustomPrint.photos_no;
  const identityName = getIdentityName(identity);
  const [basePhoto, teamPhoto] = configuration?.photos ?? [];
  const { preview_image: previewImage } = albumCustomPrint?.custom_print ?? {};
  const classes = classNames({
    'checkout-cart-item checkout-album-custom-print-cart-item': true,
    'checkout-prepaid-album-custom-print-cart-item': isPrepaid,
  });

  return (
    <div className={classes}>
      <div className="checkout-cart-item-content">
        {(() => {
          if (isPrepaid) {
            return (
              <div className="checkout-cart-item-figure">
                <div className="purchasing-option-item-thumb">
                  <img
                    src={previewImage?.url ?? getAsset('/plaque.svg')}
                    alt=""
                    className="pr-2"
                  />
                </div>
                <IdentityAvatar identity={identity} />
              </div>
            );
          }

          if (
            albumCustomPrint.custom_print.type === CUSTOM_PRINT_TYPE.COLLAGE
          ) {
            return (
              <div className="checkout-cart-item-figure">
                <img src={getAsset('/collage-illustration.svg')} alt="" />
                <span className="checkout-cart-item-photo-icon">
                  <CollageIcon size={20} />
                </span>
              </div>
            );
          }

          if (
            albumCustomPrint.custom_print.subtype ===
            CUSTOM_PRINT_SUBTYPE.SPECIALTY_PRINT
          ) {
            return (
              <CartItemPhotoPreview
                photo={basePhoto?.photo}
                watermarkSettings={watermarkSettings}
                className="mr-2"
              >
                {teamPhoto?.photo && (
                  <span className="checkout-cart-item-photo-img-secondary">
                    <img
                      alt=""
                      src={
                        getThumbBySize(
                          teamPhoto?.photo?.thumbnail_urls,
                          IMAGE_SIZE_CODE.SMALL2X,
                        )?.url
                      }
                    />
                  </span>
                )}
              </CartItemPhotoPreview>
            );
          }

          if (item.configuration?.accountCard) {
            return <AccountCardCartItemPreview item={item} />;
          }

          return (
            <div className="checkout-cart-item-figure checkout-cart-item-avatar">
              <IdentityAvatar identity={identity}>
                <span className="checkout-cart-item-photo-icon">
                  <CustomPrintIcon color="#0e84fe" />
                </span>
              </IdentityAvatar>
            </div>
          );
        })()}

        <div className="checkout-cart-item-info">
          <span className="checkout-cart-item-title">
            <span className="text-truncate">
              {getAlbumCustomPrintName(albumCustomPrint)}
            </span>
          </span>
          <CheckoutCartItemPriceDisplay
            clientPrice={price}
            pricing={pricing}
            loading={loading}
          />
          <CartItemVendor cartItem={item} />
          <div className="checkout-cart-item-info-actions mt-2">
            <CheckoutItemQuantityInput
              onChange={value =>
                onUpdate(item.id, {
                  quantity: value,
                })
              }
              value={quantity}
            />

            <button
              className="checkout-cart-item-btn-remove"
              onClick={() => onRemove(item.id)}
            >
              <TrashIcon color="#fe5851" size={18} />
            </button>
          </div>
          {!isAlbumCustomPrintConfigurableInPlace(albumCustomPrint) &&
            albumCustomPrint.custom_print?.subtype !==
              CUSTOM_PRINT_SUBTYPE.SPECIALTY_PRINT && (
              <div className="checkout-cart-item-description">
                {albumCustomPrint.custom_print?.type !==
                  CUSTOM_PRINT_TYPE.WALDOGRAM &&
                  `Choose your fav ${photosNo} photos of ${identityName} after
                checkout.`}
              </div>
            )}
          {albumCustomPrint.custom_print?.subtype ===
            CUSTOM_PRINT_SUBTYPE.SPECIALTY_PRINT && (
            <div className="checkout-cart-item-description">
              This product will be fulfilled by the photographer.
            </div>
          )}
        </div>
      </div>
      {isAlbumCustomPrintConfigurableInPlace(albumCustomPrint) &&
        !isPrepaid && (
          <Button
            btnSize="sm"
            btnStyle="link"
            className="text-extra-bold text-nowrap"
            onClick={() => setConfigurationModalOpen(true)}
          >
            View &amp; Edit
          </Button>
        )}
      <AccountCardAlbumCustomPrintConfiguratorModal
        defaultAccountCard={item.configuration?.accountCard}
        forceOpen={configurationModalOpen && item.configuration?.accountCard}
        albumCustomPrint={albumCustomPrint}
        albumMembershipGroup={item.albumMembershipGroup}
        onClose={() => setConfigurationModalOpen(false)}
        onFinished={({ accountCard }) => {
          onUpdate(item.id, {
            configuration: { accountCard },
          });
          setConfigurationModalOpen(false);
        }}
        buttonText="Done"
        isAllPhotos={isAllPhotos}
        forceEntitlement={forceEntitlement}
        watermarkSettings={watermarkSettings}
        printBackgrounds={printBackgrounds}
      />
      <CustomPrintPlaqueConfiguratorModal
        forceOpen={configurationModalOpen && !item.configuration?.accountCard}
        albumCustomPrint={albumCustomPrint}
        identity={identity}
        initialConfiguration={item.configuration}
        renderSubmit={config => {
          return (
            <Fragment>
              {validateCustomPrintPhotoSelections(config) && (
                <Button
                  onClick={() => {
                    onUpdate(item.id, {
                      configuration: config,
                    });
                    setConfigurationModalOpen(false);
                  }}
                  btnBlock
                  className="text-black"
                >
                  Done
                </Button>
              )}
              <Button
                btnStyle="link"
                btnBlock
                onClick={() => setConfigurationModalOpen(false)}
                className="text-center mt-2"
              >
                Dismiss
              </Button>
            </Fragment>
          );
        }}
      />
    </div>
  );
};

AlbumCustomPrintCartItem.propTypes = propTypes;

export default AlbumCustomPrintCartItem;
